import { PotentialTradeStatus } from '@kwenta/sdk/types'
import { isZero } from '@kwenta/sdk/utils'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import { ERROR_MESSAGES } from 'components/ErrorNotifier'
import Error from 'components/ErrorView'
import { setOpenModal } from 'state/app/reducer'
import {
	selectCrossMarginAccount,
	selectSelectedCrossMarginPosition,
	selectCrossMarginTradeInputs,
	selectV3MarketInfo,
	selectIsV3MarketCapReached,
	selectCrossMarginTradePreview,
	selectIsFetchingCMTradePreview,
} from 'state/futures/crossMargin/selectors'
import { setTradePanelDrawerOpen } from 'state/futures/reducer'
import {
	selectLeverageSide,
	selectPendingDelayedOrder,
	selectMaxUsdSizeInput,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { previewStatusToI18nMsg } from 'utils/futures'

const SubmitCrossMarginTradeButton: React.FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const { susdSize } = useAppSelector(selectCrossMarginTradeInputs)
	const previewTrade = useAppSelector(selectCrossMarginTradePreview)
	const openOrder = useAppSelector(selectPendingDelayedOrder)
	const leverageSide = useAppSelector(selectLeverageSide)
	const maxUsdInputAmount = useAppSelector(selectMaxUsdSizeInput)
	const isMarketCapReached = useAppSelector(selectIsV3MarketCapReached)
	const marketInfo = useAppSelector(selectV3MarketInfo)
	const crossMarginAccount = useAppSelector(selectCrossMarginAccount)
	const position = useAppSelector(selectSelectedCrossMarginPosition)
	const fetchingPreview = useAppSelector(selectIsFetchingCMTradePreview)

	const orderError = useMemo(() => {
		if (previewTrade?.statusMessage && previewTrade.statusMessage !== 'Success')
			return previewTrade?.statusMessage
		return null
	}, [previewTrade?.statusMessage])

	const increasingPosition = !position?.details.side || position?.details.side === leverageSide

	const onSubmit = useCallback(() => {
		dispatch(setTradePanelDrawerOpen(false))
		if (!crossMarginAccount) {
			dispatch(setOpenModal('futures_cross_margin_onboard'))
			return
		}
		dispatch(setOpenModal('futures_confirm_cross_margin_trade'))
	}, [crossMarginAccount, dispatch])

	// TODO: Clean up errors and warnings and share logic with smart margin

	const placeOrderDisabledReason = useMemo<{
		message: string
		show?: 'warn' | 'error'
	} | null>(() => {
		if (orderError) {
			return { message: orderError, show: 'error' }
		}

		if (previewTrade && previewTrade.status !== PotentialTradeStatus.OK) {
			const i18nMessage = previewStatusToI18nMsg(previewTrade.status)
			return { message: t(i18nMessage), show: 'error' }
		}

		// TODO: Validate liquidation from preview
		const canLiquidate = false
		if (canLiquidate) {
			return {
				show: 'warn',
				message: `Position can be liquidated`,
			}
		}

		if (marketInfo?.isSuspended)
			return {
				show: 'warn',
				message: `Market suspended`,
			}
		if (isMarketCapReached && increasingPosition)
			return {
				show: 'warn',
				message: `Open interest limit exceeded`,
			}

		if (susdSize.gt(maxUsdInputAmount))
			return {
				show: 'warn',
				message: 'Max trade size exceeded',
			}

		if (isZero(susdSize)) {
			return { message: 'Trade size required' }
		}
		if (!!openOrder && !openOrder.isStale) {
			return {
				show: 'warn',
				message: ERROR_MESSAGES.ORDER_PENDING,
			}
		}

		return null
	}, [
		t,
		previewTrade,
		susdSize,
		openOrder,
		orderError,
		marketInfo?.isSuspended,
		maxUsdInputAmount,
		isMarketCapReached,
		increasingPosition,
	])

	return (
		<>
			<div>
				<ManagePositionContainer>
					<PlaceOrderButton
						data-testid="trade-panel-submit-button"
						noOutline
						fullWidth
						loading={fetchingPreview}
						variant={leverageSide}
						disabled={!!placeOrderDisabledReason || fetchingPreview}
						onClick={onSubmit}
					>
						{t(
							position?.details
								? 'futures.market.trade.button.modify-position'
								: 'futures.market.trade.button.open-position'
						)}
					</PlaceOrderButton>
				</ManagePositionContainer>
			</div>

			{placeOrderDisabledReason?.show ? (
				<Error
					message={placeOrderDisabledReason.message}
					messageType={placeOrderDisabledReason.show}
				/>
			) : null}
		</>
	)
}

const ManagePositionContainer = styled.div`
	display: flex;
	grid-gap: 15px;
	margin-bottom: 16px;
`

const PlaceOrderButton = styled(TransactionButton)`
	font-size: 16px;
	height: 55px;
	text-align: center;
	white-space: normal;
`

export default SubmitCrossMarginTradeButton
